import { useEffect, useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export const DepotBasedRevenueReport = memo(({}) => {
  const { t } = useTranslation();
  var currentTime = new Date();
  var currentyear = currentTime.getFullYear();
  var currentMonth = currentTime.getUTCMonth() + 1;
  const [reportType, setReportType] = useState("monthly");
  const [typeFilter, setTypeFilter] = useState("total revenue");
  const getCurrentFiscalYear = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    // Fiscal year starts in September
    const startYear = month >= 9 ? year : year - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };
  const [period, setPeriod] = useState(getCurrentFiscalYear());
  const [isLoading, setIsLoading] = useState(true);
  const [viewFormula, setViewFormula] = useState("");
  const [mainDepotList, setMainDepotList] = useState([]);
  const [mainDepot, setMainDepot] = useState("");
  // Custom plugin to display "No data found"
  const noDataPlugin = {
    id: "noDataPlugin",
    beforeDraw: (chart) => {
      const { datasets } = chart.data;
      const hasData = datasets.some((dataset) => dataset.data.length > 0);
      if (!hasData) {
        const ctx = chart.ctx;
        const { width, height } = chart;
        ctx.save();
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "16px Arial";
        ctx.fillStyle = "#eb9f9d";
        ctx.fillText(t("no_data_filtered_search_txt"), width / 2, height / 2);
        ctx.restore();
      }
    },
  };

  const [topSellerChartData, setTopSellerChartData] = useState({
    labels: [],
    datasets: [
      {
        label: t("depot_based_revenue_report_txt"),
        data: [],
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
    ],
  });

  const groupBySubDepot = (data) => {
    return data.reduce((acc, item) => {
      const subDepot = mainDepot ? item._id.subDepot : item._id.mainDepot;
      if (!acc[subDepot]) {
        acc[subDepot] = [];
      }
      acc[subDepot].push(item);
      return acc;
    }, {});
  };

  const getRandomColor = () => {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const quarterNames = {
    1: t("quarter_sep_nov"),
    2: t("quarter_dec_feb"),
    3: t("quarter_mar_may"),
    4: t("quarter_jun_aug"),
  };

  const getDataForPeriod = (result, labels, reportType) => {
    return result.map((item) => {
      return {
        label: `${item?.subDepot}`,
        data: labels.map((label) => {
          const foundItem = item?.data.find((itemValue) => {
            if (reportType === "weekly") {
              const date = new Date(itemValue._id.week);
              const day = String(date.getDate()).padStart(2, "0");
              const month = String(date.getMonth() + 1).padStart(2, "0");
              const year = date.getFullYear();
              const formatted = `${day}/${month}/${year}`;
              return label === formatted;
            } else if (reportType === "monthly") {
              const monthYearVal = `${itemValue._id.month}/${itemValue._id.year}`;
              return label === monthYearVal;
            } else if (reportType === "yearly") {
              return label === `${itemValue._id.year}`;
            }
          });
          if (mainDepot) {
            setViewFormula(
              foundItem
                ? typeFilter === "total revenue"
                  ? foundItem.amount.toFixed(2)
                  : foundItem.orderCount || 0
                : 0,
            );
            return foundItem
              ? typeFilter === "total revenue"
                ? foundItem.amount.toFixed(2)
                : foundItem.orderCount || 0
              : 0;
          } else {
            setViewFormula(
              foundItem
                ? typeFilter === "total revenue"
                  ? foundItem.amount.toFixed(2)
                  : foundItem.amount || 0
                : 0,
            );
            return foundItem
              ? typeFilter === "total revenue"
                ? foundItem.amount.toFixed(2)
                : foundItem.amount || 0
              : 0;
          }
        }),
        borderColor: getRandomColor(),
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      };
    });
  };

  const getUniqueLabels = (data, reportType) => {
    const labelSet = new Set();
    data.forEach((item) => {
      if (reportType === "weekly") {
        const date = new Date(item._id.week);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        labelSet.add(`${day}/${month}/${year}`);
      } else if (reportType === "monthly") {
        labelSet.add(`${item._id.month}/${item._id.year}`);
      } else if (reportType === "yearly") {
        labelSet.add(`${item._id.year}`);
      }
    });
    return Array.from(labelSet);
  };

  const getDataForQuarter = (result, labels) => {
    return result.map((item) => {
      return {
        label: `${item?.subDepot}`,
        data: labels.map((label) => {
          const quarterKey = Object.keys(quarterNames).find(
            (key) => quarterNames[key] === label,
          );
          const foundItem = item?.data.find(
            (itemValue) => itemValue._id.quarter == quarterKey,
          );
          if (mainDepot) {
            return foundItem
              ? typeFilter === "total revenue"
                ? foundItem.amount.toFixed(2)
                : foundItem.orderCount || 0
              : 0;
          } else {
            return foundItem
              ? typeFilter === "total revenue"
                ? foundItem.amount.toFixed(2)
                : foundItem.amount || 0
              : 0;
          }
        }),
        borderColor: getRandomColor(),
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      };
    });
  };

  const extractLabels = (data) => {
    const uniqueQuarters = [...new Set(data.map((item) => item._id.quarter))];
    return uniqueQuarters.sort().map((q) => quarterNames[q]);
  };

  useEffect(() => {
    setIsLoading(true);
    let URL = BaseUrl + `admin/getdepots`;
    http
      .get(URL)
      .then((response) => {
        setIsLoading(false);
        let responseData = response.data.data;
        const uniqueDepots = Array.from(
          new Set(responseData.map((item) => item.mainDepot)),
        ).map((mainDepot) =>
          responseData.find((item) => item.mainDepot === mainDepot),
        );
        setMainDepotList(uniqueDepots);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (reportType !== "quarterly") {
      const URL3 = BaseUrl + "admin/depotbasedrevenuereport";
      http
        .post(URL3, { reportType, period })
        .then((response) => {
          setIsLoading(false);
          const data =
            response?.data?.data.filter((item) => {
              if (mainDepot) {
                return item._id.mainDepot === mainDepot;
              }
              return true; // No filter applied
            }) || [];
          const chartData = groupBySubDepot(data);
          const labels = getUniqueLabels(data, reportType);
          // If mainDepot is selected, group by subDepot; otherwise, group by mainDepot and sum subDepot amounts per month
          let result;
          if (mainDepot) {
            // Group by subDepot as before
            result = Object.keys(chartData).map((key) => ({
              subDepot: key,
              data: chartData[key],
            }));
          } else {
            // Group by mainDepot and sum all subDepot amounts for each month/year
            // chartData keys are mainDepot names
            result = Object.keys(chartData).map((mainDepotKey) => {
              // Flatten all data for this mainDepot (all subDepots)
              const allData = chartData[mainDepotKey].flat
                ? chartData[mainDepotKey].flat()
                : [].concat(...chartData[mainDepotKey]);
              // Group by month/year
              const grouped = {};
              allData.forEach((item) => {
                let key;
                switch (reportType) {
                  case "monthly":
                    key = `${item._id.month}/${item._id.year}`;
                    break;
                  case "yearly":
                    key = `${item._id.year}`;
                    break;
                  case "weekly":
                    key = `${item._id.week}`;
                    break;
                  default:
                    key = "";
                }
                grouped[key] =
                  (grouped[key] || 0) +
                  (typeFilter === "total revenue"
                    ? item.amount
                    : item.orderCount || 0);
              });
              // Convert to array of {_id: {month, year}, amount}
              const data = Object.entries(grouped).map(([key, value]) => {
                const parts = key.split("/");
                switch (reportType) {
                  case "monthly":
                    return {
                      _id: { month: parts[0], year: parts[1] },
                      amount: value,
                    };
                  case "yearly":
                    return { _id: { year: parts[0] }, amount: value };
                  case "weekly":
                    return { _id: { week: parts[0] }, amount: value };
                  default:
                    return {};
                }
              });
              return {
                subDepot: mainDepotKey,
                data,
              };
            });
          }
          const datasets = getDataForPeriod(result, labels, reportType);
          setTopSellerChartData({
            labels,
            datasets,
          });
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      let URL3 = BaseUrl + "admin/depotbasedrevenuereport";
      http
        .post(URL3, { reportType, period })
        .then((response) => {
          setIsLoading(false);
          const data =
            response?.data?.data.filter((item) => {
              if (mainDepot) {
                return item._id.mainDepot === mainDepot;
              }
              return true; // No filter applied
            }) || [];
          const chartData = groupBySubDepot(data);
          const labels = extractLabels(data);
          // If mainDepot is selected, group by subDepot; otherwise, group by mainDepot and sum subDepot amounts per month
          let result;
          if (mainDepot) {
            // Group by subDepot as before
            result = Object.keys(chartData).map((key) => ({
              subDepot: key,
              data: chartData[key],
            }));
          } else {
            // Group by mainDepot and sum all subDepot amounts for each month/year
            // chartData keys are mainDepot names
            result = Object.keys(chartData).map((mainDepotKey) => {
              // Flatten all data for this mainDepot (all subDepots)
              const allData = chartData[mainDepotKey].flat
                ? chartData[mainDepotKey].flat()
                : [].concat(...chartData[mainDepotKey]);
              // Group by quarter
              const grouped = {};
              allData.forEach((item) => {
                const key = `${item._id.quarter}`;
                if (!grouped[key]) {
                  grouped[key] = 0;
                }
                grouped[key] +=
                  typeFilter === "total revenue"
                    ? item.amount
                    : item.orderCount || 0;
              });
              // Convert to array of {_id: {quarter}, amount}
              const data = Object.entries(grouped).map(([quarter, value]) => {
                return {
                  _id: { quarter },
                  amount: value,
                };
              });
              return {
                subDepot: mainDepotKey,
                data,
              };
            });
          }
          const datasets = getDataForQuarter(result, labels);
          setTopSellerChartData({
            labels,
            datasets,
          });
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  }, [reportType, period, mainDepot, typeFilter]);

  const changePeriodHandler = (e) => {
    setPeriod(e.target.value);
  };

  const changeMainDepotHandler = (e) => {
    setMainDepot(e.target.value);
  };

  const changeTypeFilterHandler = (e) => {
    setTypeFilter(e.target.value);
  };

  const changeHandler = (e) => {
    setReportType(e.target.value);
  };

  const options = [];
  for (let i = currentyear; i >= 2023; i--) {
    if (i === currentyear && currentMonth < 9) {
      continue;
    }
    const year = i;
    options.push(
      <option value={year + "-" + (year + 1)}>
        {year + "-" + (year + 1)}
      </option>,
    );
  }

  function centerSinglePointDatasets(chartData) {
    const labels = chartData.labels;
    const updatedDatasets = chartData.datasets.map((dataset) => {
      const validPoints = dataset.data
        .map((val, i) => ({ val, i }))
        .filter(({ val }) => val !== 0 && val !== null);
      if (validPoints.length === 1) {
        const onlyIndex = validPoints[0].i;
        const centeredData = labels.map((_, i) =>
          i === onlyIndex ? validPoints[0].val : null,
        );
        return {
          ...dataset,
          data: centeredData,
          spanGaps: false, // don't draw line through nulls
          pointRadius: 4,
          pointHoverRadius: 6,
        };
      }
      return dataset;
    });
    return { ...chartData, datasets: updatedDatasets };
  }

  let loadingbuffer = isLoading ? "loading-buffer" : "";

  return (
    <div className={`chart-container chart-week-revenue ${loadingbuffer}`}>
      <div className="chart-Hdr">
        <div className="chart-Hdr-txt">
          <h2 style={{ textAlign: "left" }}>
            {typeFilter === "total revenue"
              ? t("depot_based_revenue_report_txt")
              : t("depot_based_order_report_txt")}
          </h2>
        </div>
        <div className="chart-fltr">
          <div className="chart-fltr-right">
            {
              <select
                style={{ width: "250px" }}
                name="mainDepot"
                value={mainDepot}
                onChange={(e) => changeMainDepotHandler(e)}
              >
                <option value="">{t("select_main_depot_txt")}</option>
                {mainDepotList.map((item) => (
                  <option key={item.mainDepot} value={item.mainDepot}>
                    {item.mainDepot}
                  </option>
                ))}
              </select>
            }
            {
              <select
                value={typeFilter}
                onChange={(e) => changeTypeFilterHandler(e)}
              >
                <option value={"total revenue"}>{t("revenue_txt")}</option>
                <option value={"Order Count"}>{t("order_count_txt")}</option>
              </select>
            }
            <select
              style={{ width: "150px" }}
              value={reportType}
              onChange={(e) => changeHandler(e)}
            >
              <option value={"weekly"}>{t("current_week_txt")}</option>
              <option value={"monthly"}>{t("monthly_txt")}</option>
              <option value={"quarterly"}>{t("quarterly_txt")}</option>
              <option value={"yearly"}>{t("fy_data_txt")}</option>
            </select>
            {(reportType === "monthly" ||
              reportType === "quarterly" ||
              reportType === "yearly") && (
              <select
                style={{ width: "170px" }}
                value={period}
                onChange={(e) => changePeriodHandler(e)}
              >
                {options}
              </select>
            )}
          </div>
        </div>
      </div>
      {topSellerChartData && (
        <Line
          data={centerSinglePointDatasets(topSellerChartData)}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: true,
              },
              title: {
                display: topSellerChartData.labels.length > 0, // Only show title if there are labels
                text: mainDepot
                  ? t("local_depots_of_txt", { depot: mainDepot })
                  : t("main_depots_txt"),
              },
              datalabels: {
                display: false,
                font: {
                  weight: "bold",
                },
                anchor: "end",
                align: "top",
                formatter: function (value) {
                  return typeFilter === "total revenue" ? "£" : "#" + value;
                },
              },
              tooltip: {
                callbacks: {
                  title: (tooltipItems) => {
                    const index = tooltipItems[0].label;

                    return typeFilter === "total revenue"
                      ? t("depot_based_revenue_for_txt", { period: index })
                      : t("depot_based_order_count_for_txt", { period: index });
                  },
                  label: (tooltipItem) => {
                    const subDepot = tooltipItem.dataset.label;
                    if (typeFilter === "total revenue") {
                      return `${subDepot} (£): ${tooltipItem.formattedValue}`;
                    }
                    return `${subDepot} (#): ${tooltipItem.formattedValue}`;
                  },
                },
              },
            },
            scales: {
              x: {
                offset: true,
                grid: {
                  display: false,
                },
                ticks: {
                  align: "center",
                },
              },
              y: {
                beginAtZero: true,
              },
            },
          }}
        />
      )}
    </div>
  );
});
